// Metric card component as specified in appmark.txt
import React from "react";
import { LucideIcon } from "lucide-react";

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  color?: "blue" | "green" | "yellow" | "red" | "gray";
  loading?: boolean;
  className?: string;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon: Icon,
  trend,
  color = "blue",
  loading = false,
  className = "",
}) => {
  const getColorClasses = () => {
    switch (color) {
      case "green":
        return {
          bg: "bg-green-50 dark:bg-green-900/20",
          icon: "text-green-600 dark:text-green-400",
          border: "border-green-200 dark:border-green-800",
        };
      case "yellow":
        return {
          bg: "bg-yellow-50 dark:bg-yellow-900/20",
          icon: "text-yellow-600 dark:text-yellow-400",
          border: "border-yellow-200 dark:border-yellow-800",
        };
      case "red":
        return {
          bg: "bg-red-50 dark:bg-red-900/20",
          icon: "text-red-600 dark:text-red-400",
          border: "border-red-200 dark:border-red-800",
        };
      case "gray":
        return {
          bg: "bg-gray-50 dark:bg-gray-900/20",
          icon: "text-gray-600 dark:text-gray-400",
          border: "border-gray-200 dark:border-gray-700",
        };
      default:
        return {
          bg: "bg-blue-50 dark:bg-blue-900/20",
          icon: "text-blue-600 dark:text-blue-400",
          border: "border-blue-200 dark:border-blue-800",
        };
    }
  };

  const colorClasses = getColorClasses();

  if (loading) {
    return (
      <div
        className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 border ${colorClasses.border} ${className}`}
      >
        <div className="animate-pulse">
          <div className="flex items-center">
            <div className={`p-2 rounded-md ${colorClasses.bg}`}>
              <div className="h-6 w-6 bg-gray-300 dark:bg-gray-600 rounded" />
            </div>
            <div className="ml-4 flex-1">
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-20 mb-2" />
              <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-16" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 border ${colorClasses.border} ${className}`}
    >
      <div className="flex items-center">
        <div className={`p-2 rounded-md ${colorClasses.bg}`}>
          <Icon className={`h-6 w-6 ${colorClasses.icon}`} />
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {title}
          </p>
          <div className="flex items-baseline">
            <p className="text-2xl font-semibold text-gray-900 dark:text-white">
              {value}
            </p>
            {trend && (
              <p
                className={`ml-2 text-sm font-medium ${
                  trend.isPositive
                    ? "text-green-600 dark:text-green-400"
                    : "text-red-600 dark:text-red-400"
                }`}
              >
                {trend.isPositive ? "+" : ""}
                {trend.value}%
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
